import React, { useState, useRef, useEffect } from 'react';

/**
 * Default information icon component using inline SVG
 */
const InformationCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
    />
  </svg>
);

/**
 * Props interface for the Tooltip component
 * @property content - The text content to display in the tooltip
 * @property icon - Optional custom icon component to render as the trigger
 * @property iconClassName - Optional CSS classes for the icon
 * @property position - Preferred position of the tooltip relative to the trigger
 * @property maxWidth - Maximum width of the tooltip in pixels
 */
interface TooltipProps {
  content: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: number;
}

/**
 * Interface for tooltip positioning calculations
 */
interface TooltipPosition {
  top: number;
  left: number;
  position: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Reusable Tooltip component with click-to-show behavior and smart positioning
 * 
 * Features:
 * - Click to show/hide (not hover-based)
 * - Click outside to dismiss
 * - Smart positioning to prevent cutoff
 * - Customizable icon and styling
 * - Responsive design with Tailwind classes
 */
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  icon,
  iconClassName = "w-5 h-5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
  position = 'top',
  maxWidth = 300
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [calculatedPosition, setCalculatedPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    position
  });
  
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  /**
   * Calculate optimal tooltip position to prevent cutoff
   */
  const calculatePosition = (): TooltipPosition => {
    if (!triggerRef.current || !tooltipRef.current) {
      return { top: 0, left: 0, position };
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    const spacing = 8; // Gap between tooltip and trigger
    
    // Calculate positions for each direction
    const positions = {
      top: {
        top: triggerRect.top + scrollY - tooltipRect.height - spacing,
        left: triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2,
        position: 'top' as const
      },
      bottom: {
        top: triggerRect.bottom + scrollY + spacing,
        left: triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2,
        position: 'bottom' as const
      },
      left: {
        top: triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2,
        left: triggerRect.left + scrollX - tooltipRect.width - spacing,
        position: 'left' as const
      },
      right: {
        top: triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2,
        left: triggerRect.right + scrollX + spacing,
        position: 'right' as const
      }
    };

    // Check if preferred position fits
    const preferredPos = positions[position];
    const fitsVertically = preferredPos.top >= scrollY && 
                          preferredPos.top + tooltipRect.height <= scrollY + viewportHeight;
    const fitsHorizontally = preferredPos.left >= scrollX && 
                            preferredPos.left + tooltipRect.width <= scrollX + viewportWidth;

    if (fitsVertically && fitsHorizontally) {
      return preferredPos;
    }

    // Try other positions if preferred doesn't fit
    const fallbackOrder = ['top', 'bottom', 'right', 'left'] as const;
    
    for (const pos of fallbackOrder) {
      const candidate = positions[pos];
      const candidateFitsVertically = candidate.top >= scrollY && 
                                     candidate.top + tooltipRect.height <= scrollY + viewportHeight;
      const candidateFitsHorizontally = candidate.left >= scrollX && 
                                       candidate.left + tooltipRect.width <= scrollX + viewportWidth;
      
      if (candidateFitsVertically && candidateFitsHorizontally) {
        return candidate;
      }
    }

    // If nothing fits perfectly, adjust the preferred position to fit within viewport
    let adjustedPos = { ...preferredPos };
    
    // Adjust horizontal position
    if (adjustedPos.left < scrollX) {
      adjustedPos.left = scrollX + spacing;
    } else if (adjustedPos.left + tooltipRect.width > scrollX + viewportWidth) {
      adjustedPos.left = scrollX + viewportWidth - tooltipRect.width - spacing;
    }
    
    // Adjust vertical position
    if (adjustedPos.top < scrollY) {
      adjustedPos.top = scrollY + spacing;
    } else if (adjustedPos.top + tooltipRect.height > scrollY + viewportHeight) {
      adjustedPos.top = scrollY + viewportHeight - tooltipRect.height - spacing;
    }
    
    return adjustedPos;
  };

  /**
   * Handle click on tooltip trigger
   */
  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsVisible(!isVisible);
  };

  /**
   * Handle outside clicks to close tooltip
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        tooltipRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible]);

  /**
   * Calculate position when tooltip becomes visible
   */
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        setCalculatedPosition(calculatePosition());
      }, 0);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, position]);

  /**
   * Recalculate position on window resize
   */
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        setCalculatedPosition(calculatePosition());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible, position]);

  /**
   * Get arrow classes based on tooltip position
   */
  const getArrowClasses = (): string => {
    const baseClasses = "absolute w-2 h-2 bg-gray-900 dark:bg-gray-700 transform rotate-45";
    
    switch (calculatedPosition.position) {
      case 'top':
        return `${baseClasses} -bottom-1 left-1/2 transform -translate-x-1/2`;
      case 'bottom':
        return `${baseClasses} -top-1 left-1/2 transform -translate-x-1/2`;
      case 'left':
        return `${baseClasses} -right-1 top-1/2 transform -translate-y-1/2`;
      case 'right':
        return `${baseClasses} -left-1 top-1/2 transform -translate-y-1/2`;
      default:
        return `${baseClasses} -bottom-1 left-1/2 transform -translate-x-1/2`;
    }
  };

  return (
    <div className="relative inline-block">
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={handleTriggerClick}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-full p-1 transition-colors duration-200"
        aria-label="Show tooltip"
        type="button"
      >
        {icon || <InformationCircleIcon className={iconClassName} />}
      </button>

      {/* Tooltip Content */}
      {isVisible && (
        <div
          ref={tooltipRef}
          className="fixed z-50 px-3 py-2 text-sm text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600"
          style={{
            top: calculatedPosition.top,
            left: calculatedPosition.left,
            maxWidth: `${maxWidth}px`,
            zIndex: 9999
          }}
        >
          {/* Arrow */}
          <div className={getArrowClasses()} />
          
          {/* Content */}
          <div className="relative z-10 leading-relaxed">
            {content}
          </div>
        </div>
      )}
    </div>
  );
}; 